if (!customElements.get("persuva-addons")) {
  class PersuvaAddons extends HTMLElement {
    constructor() {
      super();
      this.checkbox = this.querySelector(".shipping-protection__checkbox");
      this.variantId = this.dataset.variantId;
      this.productId = this.dataset.productId;
      this.isPreselected = this.dataset.preselected === "true";
      
      this.isProcessing = false;
      this.currentCartState = this.dataset.inCart === "true";
      
      this.boundHandleToggle = this.handleToggle.bind(this);
      this.boundSyncWithCart = this.syncWithCart.bind(this);
      this.boundHandleStorageChange = this.handleStorageChange.bind(this);
      this.boundHandleProtectionStorageChange = this.handleProtectionStorageChange.bind(this);
      
      this.init();
    }

    init() {
      if (!this.checkbox || !this.variantId) {
        return;
      }

      this.checkbox.addEventListener("change", this.boundHandleToggle);
      
      this.storageKey = `protection-state-${this.variantId}`;
      window.addEventListener('storage', this.boundHandleStorageChange);
      document.addEventListener('protection:storage-change', this.boundHandleProtectionStorageChange);
      
      this.syncWithLocalStorage();
      
      document.addEventListener("cart:updated", this.boundSyncWithCart);
      document.addEventListener("cart:refresh", this.boundSyncWithCart);
      
      if (typeof PUB_SUB_EVENTS !== "undefined" && typeof subscribe !== "undefined") {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source !== 'protection-addon') {
            this.syncWithCart();
          }
        });
      }
    }

    connectedCallback() {
      this.setAttribute("role", "region");
      this.setAttribute("aria-label", "Product Add-ons");
    }

    disconnectedCallback() {
      document.removeEventListener("cart:updated", this.boundSyncWithCart);
      document.removeEventListener("cart:refresh", this.boundSyncWithCart);
      window.removeEventListener('storage', this.boundHandleStorageChange);
      document.removeEventListener('protection:storage-change', this.boundHandleProtectionStorageChange);
      
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
      }
    }

    async handleToggle(event) {
      if (this.isProcessing) {
        event.preventDefault();
        this.checkbox.checked = !this.currentCartState;
        return;
      }

      const shouldAdd = event.target.checked;
      
      this.currentCartState = shouldAdd;
      this.updateLocalStorage(shouldAdd);
      
      try {
        this.setLoading(true);
        
        if (shouldAdd) {
          await this.addToCart();
        } else {
          await this.removeFromCart();
        }
        
        await this.refreshCartUI();
        
      } catch (error) {
        console.error('Protection Addon Error:', error);
        this.checkbox.checked = !shouldAdd;
        this.currentCartState = !shouldAdd;
        this.updateLocalStorage(!shouldAdd);
        this.showError(error.message || 'Failed to update protection');
      } finally {
        this.setLoading(false);
      }
    }

    async addToCart() {
      const currentCart = await this.getCurrentCart();
      
      const existingItem = currentCart.items.find(item => 
        item.variant_id == this.variantId
      );
      
      if (existingItem) {
        return;
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: this.variantId,
          quantity: 1
        })
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Add to cart failed:', error, 'Variant ID:', this.variantId);
        throw new Error(error.message || error.description || 'Failed to add protection');
      }

      await response.json();
      this.currentCartState = true;
    }

    async removeFromCart() {
      const currentCart = await this.getCurrentCart();
      const existingItem = currentCart.items.find(item => 
        item.variant_id == this.variantId
      );
      
      if (!existingItem) {
        this.currentCartState = false;
        return;
      }

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: this.variantId,
          quantity: 0
        })
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Remove from cart failed:', error, 'Variant ID:', this.variantId);
        throw new Error(error.message || error.description || 'Failed to remove protection');
      }

      await response.json();
      this.currentCartState = false;
    }

    async getCurrentCart() {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }
      return await response.json();
    }

    async syncWithCart() {
      if (this.isProcessing) return;

      try {
        const cart = await this.getCurrentCart();
        const isInCart = cart.items.some(item => 
          item.variant_id == this.variantId
        );
        
        if (this.currentCartState !== isInCart) {
          this.currentCartState = isInCart;
          
          if (this.checkbox) {
            this.checkbox.checked = isInCart;
          }
          
          this.updateLocalStorage(isInCart);
        }
        
      } catch (error) {
        console.error('Error syncing with cart:', error);
      }
    }

    handleStorageChange(event) {
      if (this.isProcessing) return;
      
      if (event.key === this.storageKey && event.newValue !== null) {
        const shouldBeChecked = event.newValue === 'true';
        if (this.checkbox && this.checkbox.checked !== shouldBeChecked) {
          this.checkbox.checked = shouldBeChecked;
          this.currentCartState = shouldBeChecked;
        }
      }
    }

    handleProtectionStorageChange(event) {
      if (this.isProcessing) return;
      
      if (event.detail.key === this.storageKey) {
        const shouldBeChecked = event.detail.newValue === 'true';
        if (this.checkbox && this.checkbox.checked !== shouldBeChecked) {
          this.checkbox.checked = shouldBeChecked;
          this.currentCartState = shouldBeChecked;
        }
      }
    }

    updateLocalStorage(isActive) {
      try {
        localStorage.setItem(this.storageKey, isActive.toString());
        
        document.dispatchEvent(new CustomEvent('protection:storage-change', {
          detail: {
            key: this.storageKey,
            newValue: isActive.toString(),
            variantId: this.variantId
          }
        }));
      } catch (error) {
        console.warn('Could not update localStorage:', error);
      }
    }

    syncWithLocalStorage() {
      try {
        const storedState = localStorage.getItem(this.storageKey);
        if (storedState !== null) {
          const shouldBeChecked = storedState === 'true';
          if (this.checkbox) {
            this.checkbox.checked = shouldBeChecked;
            this.currentCartState = shouldBeChecked;
          }
        }
      } catch (error) {
        console.warn('Could not read from localStorage:', error);
      }
    }

    reinitialize() {
      if (this.checkbox) {
        this.checkbox.removeEventListener("change", this.boundHandleToggle);
      }
      
      this.checkbox = this.querySelector(".shipping-protection__checkbox");
      this.currentCartState = this.dataset.inCart === "true";
      
      if (this.checkbox) {
        this.checkbox.addEventListener("change", this.boundHandleToggle);
        
        this.syncWithLocalStorage();
        this.syncWithCart();
      }
    }

    dispatchCartEvents() {
      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true
      }));
      
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { 
          source: 'protection-addon',
          variantId: this.variantId 
        });
      }
    }

    async refreshCartUI() {
      try {
        await this.refreshCartDrawerSections();
      } catch (error) {
        console.error('Protection Addon: Error refreshing cart UI:', error);
      }
    }

    async refreshCartDrawerSections() {
      try {
        const sectionsToRender = [
          { id: "CartDrawer", section: "cart-drawer", selector: ".drawer__inner" },
          { id: "cart-icon-bubble", section: "cart-icon-bubble", selector: ".shopify-section" }
        ];
        
        const response = await fetch('/cart.js');
        if (!response.ok) {
          this.dispatchCartEvents();
          return;
        }
        
        const cart = await response.json();
        
        const sectionsUrl = `${window.location.pathname}?${sectionsToRender.map(s => `sections[]=${s.section}`).join('&')}`;
        const sectionsResponse = await fetch(sectionsUrl);
        
        if (!sectionsResponse.ok) {
          this.dispatchCartEvents();
          return;
        }
        
        const sectionsData = await sectionsResponse.json();
        
        sectionsToRender.forEach(section => {
          const element = document.getElementById(section.id);
          if (element && sectionsData[section.section]) {
            const targetElement = section.selector ? 
              element.querySelector(section.selector) || element : 
              element;
              
            const newHTML = this.getSectionInnerHTML(sectionsData[section.section], section.selector);
            if (newHTML) {
              targetElement.innerHTML = newHTML;
            }
          }
        });
        
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          cartDrawer.classList.toggle('is-empty', cart.item_count === 0);
        }
        
      } catch (error) {
        console.error('Protection Addon: Error refreshing cart drawer sections:', error);
        this.dispatchCartEvents();
      }
    }
    
    getSectionInnerHTML(html, selector = '.shopify-section') {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const element = doc.querySelector(selector);
      return element ? element.innerHTML : null;
    }

    setLoading(loading) {
      this.isProcessing = loading;
      
      if (this.checkbox) {
        this.checkbox.disabled = loading;
      }

      this.classList.toggle("loading", loading);
      this.setAttribute("aria-busy", loading.toString());
    }

    showError(message) {
      const errorElement = this.querySelector(".shipping-protection__error") || this.createErrorElement();
      errorElement.textContent = message;
      errorElement.style.display = "block";

      setTimeout(() => {
        errorElement.style.display = "none";
      }, 5000);
    }

    createErrorElement() {
      const errorElement = document.createElement("div");
      errorElement.className = "shipping-protection__error";
      errorElement.setAttribute("role", "alert");
      this.appendChild(errorElement);
      return errorElement;
    }
  }

  customElements.define("persuva-addons", PersuvaAddons);

  document.addEventListener("DOMContentLoaded", function() {
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const protectionComponents = node.querySelectorAll ? 
                node.querySelectorAll('persuva-addons') : [];
              
              if (node.tagName === 'PERSUVA-ADDONS') {
                if (node.reinitialize) node.reinitialize();
              }
              
              protectionComponents.forEach(function(component) {
                if (component.reinitialize) {
                  setTimeout(() => component.reinitialize(), 100);
                }
              });
            }
          });
        }
      });
    });

    const cartDrawer = document.querySelector('cart-drawer-items');
    if (cartDrawer) {
      observer.observe(cartDrawer, {
        childList: true,
        subtree: true
      });
    }
  });
}